/**
 * main.ts — orchestration for the Kitchen demo.
 *
 * Two-loop structure (copied from reflex/mob):
 *   - rAF loop runs for the page lifetime (cheap; eases the agent animation).
 *   - setInterval ticks the agent at a user-tunable Hz, decoupled from the
 *     render rate.
 *
 * Lifecycle: 'idle' | 'running' | 'stopped'. When an episode finishes, a
 * fresh world from the current theme starts. URL params:
 *   ?arm=ts|greedy|linear|random  &theme=morning|evening|clash|all_day
 */

import './style.css';
import {
  START_R, START_C, N_ACTIONS, N_FEATURES, SKIP, PICKUP,
  HEADLINE_THEME, ITEM_VALUE,
  type ArmMode, type ThemeName,
} from './constants';
import { makeWorld, makeRng } from './shapes';
import { KitchenEnv } from './environment';
import { KitchenBandit, DEFAULT_BANDIT_HP } from './bandit';
import { ForestClient } from './forest_client';
import { Renderer, makeAgentAnim } from './render';
import { SidePanel, type PanelState, type PanelCallbacks } from './ui';
import { HelpModal } from './help';
import {
  DEFAULT_HARNESS_CONFIG,
  genPretrainData,
  type HarnessEvent,
} from './harness';
import { subsetAccuracy, pickupReward } from './items';

const DEFAULT_SPEED_HZ = 12;

type Phase = 'idle' | 'running' | 'stopped';

function parseArmParam(): ArmMode {
  const v = new URLSearchParams(window.location.search).get('arm');
  if (v === 'ts' || v === 'greedy' || v === 'linear' || v === 'random') return v;
  return 'ts';
}
function parseThemeParam(): ThemeName {
  const v = new URLSearchParams(window.location.search).get('theme') as ThemeName | null;
  if (v && ['morning', 'evening', 'clash', 'all_day'].includes(v)) return v;
  return HEADLINE_THEME;
}

function syncUrlParam(key: string, val: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set(key, val);
  window.history.replaceState({}, '', url);
}

function main(): void {
  const app = document.getElementById('app')!;
  app.innerHTML = '';

  const lakeCanvas = document.createElement('canvas');
  lakeCanvas.className = 'lake';
  const lakeWrap = document.createElement('div');
  lakeWrap.className = 'lake-wrap';
  lakeWrap.appendChild(lakeCanvas);
  app.appendChild(lakeWrap);

  const panelSlot = document.createElement('div');
  panelSlot.className = 'panel-slot';
  app.appendChild(panelSlot);

  // ── Tunable state ──────────────────────────────────────────────────
  let arm: ArmMode = parseArmParam();
  let theme: ThemeName = parseThemeParam();
  let speedHz = DEFAULT_SPEED_HZ;
  let phase: Phase = 'idle';
  let tickIntervalId: number | null = null;
  let runStartMs = 0;

  const hp = { ...DEFAULT_BANDIT_HP, mode: arm, seed: 0 };

  // ── Core objects ──────────────────────────────────────────────────
  const worldRng = makeRng(12345);
  let worldAndCtx = makeWorld(theme, worldRng);
  let env = new KitchenEnv(worldAndCtx.grid, worldAndCtx.ctx);
  env.reset();
  const agent = new KitchenBandit(hp);
  const forestClient = new ForestClient(agent);
  forestClient.install();
  const agentAnim = makeAgentAnim(START_R, START_C);
  const renderer = new Renderer(lakeCanvas);

  let episodeSteps = 0;
  let episodeReturn = 0;
  let episodeBasket = 0;
  let episodeAcc = 0;
  // Per-episode F1 + dup-rate accumulators (first-time decisions only).
  // Reset on each episode boundary; TP/FP/FN where positive class = should
  // PICKUP, plus a duplicate-pickup counter (must stay 0 under the
  // legal-mask constraint).
  let epTp = 0, epFp = 0, epFn = 0, epDupPickups = 0, epDupTotal = 0;
  let totalSteps = 0;
  let lastReason: 'drift' | 'cold' | 'model' = 'cold';

  const featureBuf = new Float64Array(N_ACTIONS * N_FEATURES);

  /** Per-episode F1 from the incremental TP/FP/FN counters. */
  function epF1(): number {
    const prec = epTp / Math.max(1, epTp + epFp);
    const rec = epTp / Math.max(1, epTp + epFn);
    return 2 * prec * rec / Math.max(1e-9, prec + rec);
  }

  // ── Harness state ────────────────────────────────────────────────
  let harnessWorker: Worker | null = null;
  let harnessState: PanelState['harnessState'] = {
    status: 'idle', progress: '', trainEval: null, flip: null,
  };
  // Pretrain state: true while the teacher rollout is in progress.
  let pretraining = false;

  // ── Panel ─────────────────────────────────────────────────────────
  const cb: PanelCallbacks = {
    onStart: () => startRun(),
    onStop: () => stopRun(),
    onReset: () => resetRun(),
    onSpeed: (hz) => { speedHz = hz; if (phase === 'running') restartTickTimer(); },
    onArm: (mode) => {
      arm = mode;
      hp.mode = mode;
      agent.hp.mode = mode;
      syncUrlParam('arm', mode);
    },
    onTheme: (t) => {
      theme = t;
      syncUrlParam('theme', t);
      if (phase === 'idle') regenWorld();
    },
    onNTrees: (n) => { hp.nTrees = n; agent.hp.nTrees = n; },
    onMinSamplesLeaf: (n) => { hp.minSamplesLeaf = n; agent.hp.minSamplesLeaf = n; },
    onColdStartK: (k) => { hp.coldStartK = k; agent.hp.coldStartK = k; },
    onRefitEvery: (n) => { hp.refitEvery = n; agent.hp.refitEvery = n; },
    onRunHarness: () => runHarnessButton(),
    onPretrain: () => pretrainButton(),
  };
  const panel = new SidePanel(cb, {
    arm, theme, speedHz,
    nTrees: hp.nTrees, minSamplesLeaf: hp.minSamplesLeaf,
    coldStartK: hp.coldStartK, refitEvery: hp.refitEvery,
  });
  panelSlot.appendChild(panel.el);

  // ── Help modal ────────────────────────────────────────────────────
  const helpBtn = document.createElement('button');
  helpBtn.className = 'help-btn';
  helpBtn.textContent = '?';
  helpBtn.title = 'Help';
  helpBtn.addEventListener('click', () => helpModal.toggle());
  document.body.appendChild(helpBtn);
  const helpModal = new HelpModal();

  function regenWorld(): void {
    worldAndCtx = makeWorld(theme, worldRng);
    env = new KitchenEnv(worldAndCtx.grid, worldAndCtx.ctx);
    env.reset();
    agentAnim.r = START_R; agentAnim.c = START_C;
    agentAnim.sr = START_R; agentAnim.sc = START_C;
  }

  function startRun(): void {
    if (phase === 'running') return;
    if (phase === 'stopped' || phase === 'idle') {
      agent.resetRun();
      regenWorld();
      episodeSteps = 0; episodeReturn = 0; episodeBasket = 0; episodeAcc = 0;
      epTp = 0; epFp = 0; epFn = 0; epDupPickups = 0; epDupTotal = 0;
      totalSteps = 0;
    }
    phase = 'running';
    runStartMs = performance.now();
    restartTickTimer();
  }
  function stopRun(): void {
    if (phase !== 'running') return;
    phase = 'stopped';
    if (tickIntervalId !== null) { clearInterval(tickIntervalId); tickIntervalId = null; }
  }
  function resetRun(): void {
    phase = 'idle';
    if (tickIntervalId !== null) { clearInterval(tickIntervalId); tickIntervalId = null; }
    agent.resetRun();
    regenWorld();
    episodeSteps = 0; episodeReturn = 0; episodeBasket = 0; episodeAcc = 0;
    epTp = 0; epFp = 0; epFn = 0; epDupPickups = 0; epDupTotal = 0;
    totalSteps = 0;
  }
  function restartTickTimer(): void {
    if (tickIntervalId !== null) clearInterval(tickIntervalId);
    const period = Math.max(16, Math.round(1000 / Math.max(1, speedHz)));
    tickIntervalId = window.setInterval(tick, period);
  }

  function tick(): void {
    if (env.done) {
      episodeBasket = env.basket;
      episodeAcc = subsetAccuracy(env.basket, env.ctx);
      regenWorld();
      episodeSteps = 0; episodeReturn = 0;
      epTp = 0; epFp = 0; epFn = 0; epDupPickups = 0; epDupTotal = 0;
      return;
    }
    if (env.currentItem < 0) { regenWorld(); return; }
    env.candidateFeatures(featureBuf);
    const { item, ctx, basket } = env.adjacentContext();
    const legal = env.legalActions();
    const sel = agent.select(featureBuf, legal);
    lastReason = sel.reason;
    // Counterfactual rewards for both arms. pickupReward applies the
    // duplicate penalty for already-collected item types.
    const pickupDelta = pickupReward(basket, item, ctx);
    const takenReward = sel.action === PICKUP ? pickupDelta : 0;
    // Incremental F1 + dup-rate accounting for this decision.
    const isDup = (basket & (1 << item)) !== 0;
    if (isDup) {
      epDupTotal++;
      if (sel.action === PICKUP) epDupPickups++;
    } else {
      const optimal = ITEM_VALUE[item][ctx] > 0 ? PICKUP : SKIP;
      if (optimal === PICKUP) {
        if (sel.action === PICKUP) epTp++; else epFn++;
      } else {
        if (sel.action === PICKUP) epFp++;
      }
    }
    const { done } = env.step(sel.action);
    // Observe BOTH arms counterfactually (balanced 2-arm training).
    agent.observe(
      featureBuf.slice(SKIP * N_FEATURES, (SKIP + 1) * N_FEATURES),
      SKIP, 0, sel.action, takenReward,
    );
    agent.observe(
      featureBuf.slice(PICKUP * N_FEATURES, (PICKUP + 1) * N_FEATURES),
      PICKUP, pickupDelta, sel.action, takenReward,
    );
    episodeReturn += takenReward;
    episodeSteps++;
    totalSteps++;
    agentAnim.r = env.r; agentAnim.c = env.c;
    agentAnim.prevR = env.prevR; agentAnim.prevC = env.prevC;
    if (done) {
      episodeBasket = env.basket;
      episodeAcc = subsetAccuracy(env.basket, env.ctx);
    }
  }

  // ── rAF render loop ───────────────────────────────────────────────
  function frame(t: number): void {
    renderer.frame(t, {
      env, agentAnim, theme, currentCtx: env.ctx,
      episodeSteps, episodeReturn, episodeBasket, episodeAcc,
      totalSteps, arm, phase, lastReason,
    });
    const elapsed = (performance.now() - runStartMs) / 1000;
    const ps: PanelState = {
      arm, theme, phase, speedHz,
      episodeSteps, episodeReturn, episodeBasket, episodeAcc,
      episodeF1: epF1(),
      episodeDupRate: epDupTotal > 0 ? epDupPickups / epDupTotal : 0,
      episodeDupTotal: epDupTotal,
      totalSteps, elapsed,
      dayBufferSize: agent.dayBufferSize,
      longBufferSize: agent.longBufferSize,
      nObserved: agent.nObserved,
      nDrift: agent.driftEvents.length,
      evalMode: agent.evalMode,
      headIsFit: agent.headIsFit,
      lastReason,
      lastAction: env.lastAction,
      pretraining,
      hp,
      harnessState,
    };
    panel.update(ps);
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);

  // ── Pretrain button: bootstrap the live agent's head from teacher
  // rollouts BEFORE the run starts. Lets the user watch a competent
  // (already-trained) agent from step 1, skipping the cold-start coin-
  // flipping phase. Same genPretrainData the harness uses; fed into
  // agent.pretrain which seeds the long buffer + immediately fits.
  function pretrainButton(): void {
    if (pretraining || agent.headIsFit) return;
    pretraining = true;
    // Defer the (synchronous, ~100-300ms) rollout to the next tick so the
    // button has a chance to re-render its "Pretraining…" state first.
    setTimeout(() => {
      try {
        const pt = genPretrainData(150, 999);
        agent.pretrain(pt.X, pt.y);
      } finally {
        pretraining = false;
      }
    }, 0);
  }

  // ── Harness (run-evaluation button) ───────────────────────────────
  function runHarnessButton(): void {
    if (harnessWorker) return;
    harnessState = { status: 'running', progress: 'pretraining…', trainEval: null, flip: null };
    // Generate pretrain data on the main thread (fast — ~150 short episodes),
    // then post to the harness worker with pretrainX/Y in the config.
    const pt = genPretrainData(150, 999);
    harnessState.progress = `pretrained (${pt.y.length} transitions), running…`;
    harnessWorker = new Worker(new URL('./harness_worker.ts', import.meta.url), { type: 'module' });
    harnessWorker.onmessage = (e: MessageEvent) => {
      const evt = e.data as HarnessEvent;
      if (evt.kind === 'progress') {
        harnessState.progress = `${evt.arm} seed ${evt.seed} ${evt.phase} ${evt.worldIdx + 1}/${evt.nWorlds}`;
      } else if (evt.kind === 'arm-done') {
        harnessState.progress = `${evt.arm} done`;
      } else if (evt.kind === 'done') {
        harnessState = { status: 'done', progress: 'done', trainEval: evt.trainEval, flip: evt.flip };
        if (harnessWorker) { harnessWorker.terminate(); harnessWorker = null; }
      } else if (evt.kind === 'error') {
        harnessState = { status: 'error', progress: `error: ${evt.message}`, trainEval: null, flip: null };
        if (harnessWorker) { harnessWorker.terminate(); harnessWorker = null; }
      }
    };
    const cfg = { ...DEFAULT_HARNESS_CONFIG, pretrainX: pt.X, pretrainY: pt.y };
    harnessWorker.postMessage({ kind: 'run', config: cfg }, [pt.X.buffer, pt.y.buffer]);
  }
}

main();
